import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { Button, Slider, CustomMarks } from '@repo/ui';
import { colorPalette } from '@repo/mui-config/color-palette';
import type { Currency } from '@repo/shared/types';
import { convertNumberToLocaleMoneyFormat } from '@repo/shared/helpers';

import { AdvanceAmountEditPreviewWrapper } from './advance-amount-edit-preview-wrapper';
import { useAgreementAndApprovalsContext } from '../context/use-agreement-and-approvals-context';
import type {
  AdvancePaymentDetails,
  AgreementAndApprovalsFields,
} from '../agreement-and-approvals.schema';

interface AgreementAndApprovalsSummaryEditProps {
  advancePayment: AdvancePaymentDetails;
  currency: Currency;
}
export function AdvancedAmountEdit({
  advancePayment: { min, max },
  currency,
}: AgreementAndApprovalsSummaryEditProps) {
  const { t } = useTranslation();
  const { setValue, watch } = useFormContext<AgreementAndApprovalsFields>();
  const editedAmount = watch('editedAmount');
  const originalAmount = watch('originalAmount');
  const { isAdvancePaymentCalculating, setIsEditMode, setIsAdvancePaymentCalculating } =
    useAgreementAndApprovalsContext();

  const resetEditedAmount = () => {
    setValue('editedAmount', originalAmount);
  };

  const cancelEditMode = () => {
    resetEditedAmount();
    setIsEditMode(false);
  };

  return (
    <AdvanceAmountEditPreviewWrapper testId="advanced-amount-edit-mode">
      <Stack
        alignItems="center"
        direction={{ sm: 'row', xs: 'column' }}
        gap={1}
        justifyContent="space-between"
      >
        <Typography fontWeight={700}>
          {t('loanProcess.loanSteps.agreementAndApprovals.summary.advancedAmount.title')}
        </Typography>
        <Stack direction="row" gap={0.5}>
          <Button
            data-testid="close-edit-mode-button"
            onClick={cancelEditMode}
            sx={{ textTransform: 'none' }}
            variant="text"
          >
            <Typography variant="subtitle2">
              {t('loanProcess.loanSteps.agreementAndApprovals.summary.advancedAmount.edit.cancel')}
            </Typography>
          </Button>
          <Button
            color="primary"
            data-testid="submit-amount-edit-mode"
            disabled={isAdvancePaymentCalculating}
            size="small"
            sx={{
              borderRadius: '12px',
              paddingX: 1.5,
              textTransform: 'none',
            }}
            type="submit"
            variant="contained"
          >
            <Typography color={colorPalette.white} variant="subtitle2">
              {t('loanProcess.loanSteps.agreementAndApprovals.summary.advancedAmount.edit.confirm')}
            </Typography>
          </Button>
        </Stack>
      </Stack>
      <Typography component="span" variant="h3">
        {convertNumberToLocaleMoneyFormat(editedAmount)}
        <Typography component="span" fontWeight={700} sx={{ marginLeft: 1 }}>
          {currency}
        </Typography>
      </Typography>
      <Slider
        customMarks={<CustomMarks currency={currency} max={max} min={min} />}
        data-testid="slider-editedAmount"
        max={max}
        min={min}
        name="editedAmount"
        onChangeCommitted={() => {
          setIsAdvancePaymentCalculating(false);
        }}
        slotProps={{
          root: {
            onMouseDown: () => {
              setIsAdvancePaymentCalculating(true);
            },
          },
        }}
        step={100}
        valueLabelFormat={(value) => value.toLocaleString()}
      />
    </AdvanceAmountEditPreviewWrapper>
  );
}
